import { z } from "zod";

/**
 * ExtractionSchema — Intermediate representation for AI/OCR extraction output.
 *
 * This schema captures per-field confidence scoring and processing metadata
 * as documents flow through the OCR → LLM → Heuristic pipeline stages.
 * It is NOT the final ISDM used for storage; that is InvoiceSchema.
 */

// --- Recognition Source ---

export const RecognitionSource = z.enum([
  "ocr_paddle",
  "ocr_mistral",
  "ocr_cloudflare",
  "llm",
  "heuristic",
  "manual",
  "structured_input",
]);
export type RecognitionSourceType = z.infer<typeof RecognitionSource>;

// --- Pipeline Stages ---

export const PipelineStage = z.enum([
  "uploaded",
  "ocr_pending",
  "ocr_complete",
  "llm_pending",
  "llm_complete",
  "heuristic_complete",
  "schema_mapped",
  "validated",
  "exported",
  "failed",
]);
export type PipelineStageType = z.infer<typeof PipelineStage>;

// --- Processing Metadata ---

export const ProcessingMetadataSchema = z.object({
  jobId: z.string().optional(),
  documentId: z.string().optional(),
  processedAt: z.string().datetime().optional(),
  pipelineStage: PipelineStage.optional(),
  ocrEnginesUsed: z.array(RecognitionSource).optional(),
  llmModel: z.string().optional(),
  sourceLanguage: z.string().optional(), // ISO 639-1
  pageCount: z.number().int().min(1).optional(),
  overallConfidence: z.number().min(0).max(1).optional(),
  warnings: z.array(z.string()).default([]),
});
export type ProcessingMetadata = z.infer<typeof ProcessingMetadataSchema>;

// --- Per-Field Confidence Wrapper ---
// Generic helper type for LLM/OCR extraction results.
// Not a Zod schema (can't express generics easily), used as TypeScript types only.

export interface ConfidenceField<T> {
  value: T;
  confidence?: number; // 0.0 – 1.0
  source?: RecognitionSourceType;
}

export type StringField  = ConfidenceField<string>;
export type NumberField  = ConfidenceField<number>;
export type DateField    = ConfidenceField<string>; // ISO 8601
export type BooleanField = ConfidenceField<boolean>;

/** Create a StringField with full confidence from a static value */
export function sf(value: string, source: RecognitionSourceType = "structured_input"): StringField {
  return { value, confidence: 1.0, source };
}

/** Create a NumberField */
export function nf(value: number, confidence = 1.0, source: RecognitionSourceType = "structured_input"): NumberField {
  return { value, confidence, source };
}

/** Create a DateField (ISO date) */
export function df(value: string, confidence = 1.0, source: RecognitionSourceType = "structured_input"): DateField {
  return { value, confidence, source };
}

/** Check whether a field meets a minimum confidence threshold */
export function meetsConfidenceThreshold<T>(
  field: ConfidenceField<T> | undefined,
  threshold: number,
): boolean {
  if (!field) return false;
  return (field.confidence ?? 0) >= threshold;
}

// --- Field Confidence Map ---
// A flat or nested record mapping field paths to 0.0–1.0 scores,
// as returned by the LLM alongside extracted data.

export const FieldConfidenceMapSchema = z.record(z.string(), z.union([z.number(), z.record(z.string(), z.number())]));
export type FieldConfidenceMap = z.infer<typeof FieldConfidenceMapSchema>;

// --- Extraction Result (pipeline payload enrichment) ---
// The object placed on context.payload after Node_AI_Extract and enriched by Node_Heuristic_Enrich.

export const ExtractionResultSchema = z.object({
  /** The raw ISDM extracted by the LLM (may be partial / not yet validated) */
  isdm: z.record(z.string(), z.unknown()),
  /** Per-field confidence scores returned alongside the LLM extraction */
  fieldConfidence: FieldConfidenceMapSchema.optional(),
  /** Aggregate confidence score (0.0–1.0) computed from field-level scores */
  aiConfidence: z.number().min(0).max(1).optional(),
  /** Processing metadata tracking stage, models used, warnings, etc. */
  processingMetadata: ProcessingMetadataSchema.optional(),
});
export type ExtractionResult = z.infer<typeof ExtractionResultSchema>;
